import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import warehouseService from '../../services/warehouseService';
import validateUtils from '../../utils/validate';
import { extractApiError } from '../../utils/apiError';
import Map from '../common/Map';
import SearchableSelect from '../common/SearchableSelect';

const WarehouseForm = ({ isOpen, onClose, onCancel, onSubmit, warehouse = null, warehouseId = null, loading = false }) => {
  const { t } = useTranslation();

  const STATE_OPTIONS = t('locations.states', { returnObjects: true });

  // Resolve a state string from API (may be label or value) → option value key
  const resolveStateValue = useCallback((raw) => {
    if (!raw) return '';
    const states = t('locations.states', { returnObjects: true });
    const match = states.find(
      (o) => o.value === raw || o.label.toLowerCase() === raw.toLowerCase()
    );
    return match ? match.value : raw;
  }, [t]);

  const REQUIRED = t('common.form.requiredIndicator');

  // State for fetching warehouse data
  const [currentWarehouse, setCurrentWarehouse] = useState(warehouse);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const fetchingRef = useRef(false);

  // Normalize close callback: parent may provide `onClose` or `onCancel`
  const closeHandler = useCallback(() => {
    if (typeof onClose === 'function') return onClose();
    if (typeof onCancel === 'function') return onCancel();
    return undefined;
  }, [onClose, onCancel]);

  const [form, setForm] = useState({
    name: '',
    address: '',
    state: '',
    is_active: true,
    total_capacity: '',
    google_maps_url: '',
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  // Fetch warehouse data — always calls API fresh (force: true)
  const fetchWarehouseData = useCallback(async (id) => {
    if (!id || fetchingRef.current) return null;
    fetchingRef.current = true;
    setWarehouseLoading(true);
    try {
      const result = await warehouseService.getWarehouseById(id, { force: true });
      if (result && result.success && result.data) {
        setCurrentWarehouse(result.data);
        return result.data;
      }
      console.error('Failed to fetch warehouse:', result && result.error);
      return null;
    } catch (error) {
      console.error('Error fetching warehouse:', error);
      return null;
    } finally {
      setWarehouseLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  // Reset local warehouse state when modal closes so next open always fetches fresh
  useEffect(() => {
    if (!isOpen) {
      setCurrentWarehouse(warehouse || null);
    }
  }, [isOpen, warehouse]);

  useEffect(() => {
    // Initialize when open or warehouse changes
    const activeWarehouse = currentWarehouse || warehouse;
    
    // If we have a warehouseId but no warehouse data, fetch it
    if (isOpen && warehouseId && !warehouse && !currentWarehouse) {
      fetchWarehouseData(warehouseId).then((fetchedWarehouse) => {
        if (fetchedWarehouse) {
            // Update form state with fetched data
            setForm({
              name: fetchedWarehouse.name || '',
              address: fetchedWarehouse.address || '',
              state: resolveStateValue(fetchedWarehouse.state),
              is_active: fetchedWarehouse.is_active !== undefined ? fetchedWarehouse.is_active : true,
              total_capacity: fetchedWarehouse.total_capacity || '',
              google_maps_url: fetchedWarehouse.google_maps_url || '',
            });
          }
      });
      return; // Don't continue with initialization if we're fetching
    }
    
    // Initialize form with warehouse data or defaults
    setForm({
      name: activeWarehouse?.name || '',
      address: activeWarehouse?.address || '',
      state: resolveStateValue(activeWarehouse?.state),
      is_active: activeWarehouse?.is_active !== undefined ? activeWarehouse.is_active : true,
      total_capacity: activeWarehouse?.total_capacity || '',
      google_maps_url: activeWarehouse?.google_maps_url || '',
    });
    setErrors({});
    setSubmitError('');
  }, [isOpen, warehouse, currentWarehouse, warehouseId, fetchWarehouseData, resolveStateValue]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = value;
    if (type === 'checkbox') v = checked;
    
    // Apply input filters
    if (['total_capacity'].includes(name)) {
      // Only allow numbers and decimals using validateUtils
      v = validateUtils.inputFilters.numeric(value);
    }
    
    setForm(prev => ({ ...prev, [name]: v }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    if (submitError) setSubmitError('');
  };

  const validate = () => {
    const v = {};
    
    // Name is required
    if (!form.name || !String(form.name).trim()) {
      v.name = t('warehouses.forms.fields.name.errors.required');
    }
    
    // Address is required
    if (!form.address || !String(form.address).trim()) {
      v.address = 'Address is required';
    }

    // Validate total capacity if provided (required field with numeric validation)
    if (!form.total_capacity || !String(form.total_capacity).trim()) {
      v.total_capacity = 'Total capacity is required';
    } else if (isNaN(Number(form.total_capacity)) || Number(form.total_capacity) < 0) {
      v.total_capacity = 'Total capacity must be a valid positive number';
    }
    
    
    setErrors(v);
    return Object.keys(v).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;            // guard double-submit
    console.log('Submit handler called');

    if (!validate()) {
      console.log('Validation failed, not submitting');
      return;
    }

    console.log('Validation passed, building payload...');

    setSubmitting(true);
    try {
      // Build payload
      const payload = {
        name: form.name,
        address: form.address || null,
        state: form.state || null,
        is_active: !!form.is_active,
        total_capacity: form.total_capacity ? parseFloat(form.total_capacity) : null,
        google_maps_url: form.google_maps_url || null,
      };

      setSubmitError('');
      const result = await onSubmit(payload);
      // Only close on success; on failure keep the form open with data intact.
      if (result && result.success === false) {
        const msg = typeof result.error === 'string' ? result.error : extractApiError(result.error);
        setSubmitError(msg || 'Failed to save warehouse. Please check the details and try again.');
        return;
      }
      closeHandler();
    } catch (err) {
      console.error('Error submitting warehouse form:', err);
      setSubmitError(extractApiError(err) || 'Failed to save warehouse. Please check the details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMapSelect = (locationData) => {
    if (!locationData) return;
    setForm(prev => ({ ...prev, google_maps_url: locationData.map_url || '' }));
    setMapOpen(false);
  };

  // If the form isn't open, don't render UI
  if (!isOpen) return null;

  // Class used to hide native vertical scrollbar across browsers for the modal content
  const scrollClass = 'warehouse-form-scroll';
  
  // Determine if we're in edit mode
  const isEdit = !!(currentWarehouse || warehouse);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      {/* Inline style block to target webkit scrollbar pseudo-element for this component scope */}
      <style>{`
        .${scrollClass} { -ms-overflow-style: none; scrollbar-width: none; }
        .${scrollClass}::-webkit-scrollbar { display: none; }
      `}</style>
      <div className={`w-full max-w-3xl relative bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${scrollClass}`} style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {isEdit ? t('warehouses.forms.title.edit') : t('warehouses.forms.title.add')}
          </h2>
          <button 
            onClick={closeHandler} 
            className="p-2 rounded-lg hover:opacity-75 transition-opacity" 
            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {submitting && (
            <div className="absolute inset-0 z-40 flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {isEdit ? 'Updating...' : 'Creating...'}
                </p>
              </div>
            </div>
          )}
          
          {warehouseLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>Loading warehouse details...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Name field - always visible */}
              <div>
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {t('warehouses.forms.fields.name.label')} {REQUIRED}
                </label>
                <input 
                  name="name" 
                  value={form.name} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 rounded-xl border-2" 
                  placeholder={t('warehouses.forms.fields.name.placeholder')}
                  style={{ 
                    backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                    borderColor: errors.name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border 
                  }} 
                />
                {errors.name && (
                  <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.name}</div>
                )}
              </div>

              {/* Address field - always visible */}
              <div>
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {t('warehouses.forms.fields.address.label')} {REQUIRED}
                </label>
                <textarea
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border-2"
                  placeholder={t('warehouses.forms.fields.address.placeholder')}
                  rows={3}
                  style={{
                    backgroundColor: UI_COLORS.FORMS.INPUT.background,
                    borderColor: errors.address ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border
                  }}
                />
                {errors.address && (
                  <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.address}</div>
                )}
              </div>

              {/* State field */}
              <div>
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {t('warehouses.forms.fields.state.label')}
                </label>
                <SearchableSelect
                  name="state"
                  value={form.state}
                  options={STATE_OPTIONS}
                  onChange={handleChange}
                  placeholder="Select state"
                  error={errors.state}
                />
                {errors.state && (
                  <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.state}</div>
                )}
              </div>

              {/* Total Capacity - always visible */}
              <div>
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {t('warehouses.forms.fields.total_capacity.label')} {REQUIRED}
                </label>
                <input 
                  name="total_capacity" 
                  value={form.total_capacity} 
                  onChange={handleChange} 
                  className="w-full px-4 py-3 rounded-xl border-2" 
                  placeholder={t('warehouses.forms.fields.total_capacity.placeholder')}
                  style={{ 
                    backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                    borderColor: errors.total_capacity ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border 
                  }} 
                />
                {errors.total_capacity && (
                  <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.total_capacity}</div>
                )}
              </div>

              {/* Google Maps URL field - always visible */}
              <div>
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {t('warehouses.forms.fields.google_maps_url.label')}
                </label>
                <div className="flex items-center space-x-2">
                  <input 
                    name="google_maps_url" 
                    value={form.google_maps_url} 
                    readOnly
                    className="flex-1 px-4 py-3 rounded-xl border-2" 
                    placeholder={t('warehouses.forms.fields.google_maps_url.placeholder')}
                    style={{ 
                      backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                      borderColor: UI_COLORS.FORMS.INPUT.border 
                    }} 
                  />
                  <button
                    type="button"
                    onClick={() => setMapOpen(true)}
                    className="px-4 py-3 rounded-xl font-semibold"
                    style={{ 
                      backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, 
                      color: UI_COLORS.BUTTONS.PRIMARY.text 
                    }}
                  >
                    Select Location
                  </button>
                </div>
              </div>

              {/* Active checkbox - always visible */}
              <div className="flex items-center">
                <label className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    name="is_active" 
                    checked={!!form.is_active} 
                    onChange={handleChange} 
                    className="rounded"
                  />
                  <span style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                    {t('warehouses.forms.fields.isActive.label')}
                  </span>
                </label>
              </div>

              {submitError && (
                <div
                  className="rounded-xl px-4 py-3 text-sm font-medium"
                  role="alert"
                  style={{
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
                    color: UI_COLORS.STATUS.ERROR,
                    border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.3)}`,
                  }}
                >
                  {submitError}
                </div>
              )}

              <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <button
                  type="submit"
                  disabled={loading || submitting}
                  className="px-6 py-3 rounded-xl font-semibold" 
                  style={{ 
                    backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, 
                    color: UI_COLORS.BUTTONS.PRIMARY.text 
                  }}
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" /> 
                  {isEdit ? t('warehouses.forms.buttons.submit.edit') : t('warehouses.forms.buttons.submit.add')}
                </button>
              </div>
            </div>
          )}
        </form>

        <Map
          isOpen={mapOpen}
          onClose={() => setMapOpen(false)}
          onLocationSelect={handleMapSelect}
          initialLocation={{
            map_url: form.google_maps_url || null,
            // Use address as the delivery_location for map geocoding
            address: form.address || null,
            delivery_location: form.address || null,
          }}
        />
      </div>
    </div>
  );
};

export default WarehouseForm;